/**
 * UserListPage — admin users list + search (STORY-008-007 UI / 008-011 API).
 * Read-only at MVP (role assignment is a deferred 008-001 remainder). Server-
 * side `q` search (debounced 300ms), role pills, status pill, locale-formatted
 * join date. Renders the PII-safe `AdminUserDto` only — NO phone / password.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminTable } from "@/features/admin/components/AdminTable";
import { userStatusTone } from "@/features/admin/components/tone";
import { useUsers } from "@/features/admin/users/hooks";
import { RoleEditorDrawer } from "@/features/admin/users/RoleEditorDrawer";
import type { AdminUser } from "@/features/admin/users/api";

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function UserListPage(): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const debouncedSearch = useDebounced(search, 300);

  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useUsers(debouncedSearch);

  const rows: AdminUser[] = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  );

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language || "ru", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
    [i18n.language],
  );

  const formatDate = (iso: string): string => {
    try {
      return dateFmt.format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const columns = [
    { key: "name", label: t("users.columns.name") },
    { key: "email", label: t("users.columns.email") },
    { key: "status", label: t("users.columns.status") },
    { key: "roles", label: t("users.columns.roles") },
    { key: "created", label: t("users.columns.created"), align: "right" as const },
  ];

  const rolePills = (roles: string[]): ReactElement => {
    if (roles.length === 0) {
      return <span className="text-[var(--g-secondary)]">{t("users.noRoles")}</span>;
    }
    return (
      <span className="flex flex-wrap gap-1">
        {roles.map((r) => (
          <GPill key={r} tone="neutral" size="sm">
            {r}
          </GPill>
        ))}
      </span>
    );
  };

  const statusPill = (status: string): ReactElement => (
    <GPill tone={userStatusTone(status)} size="sm">
      {t(`users.statusValue.${status}`, { defaultValue: status })}
    </GPill>
  );

  const renderCell = (u: AdminUser, key: string): ReactElement | string => {
    switch (key) {
      case "name":
        return <span className="font-medium">{u.display_name}</span>;
      case "email":
        return <span className="text-[var(--g-secondary)]">{u.email}</span>;
      case "status":
        return statusPill(u.status);
      case "roles":
        return rolePills(u.roles);
      case "created":
        return formatDate(u.created_at);
      default:
        return "";
    }
  };

  const renderMobileCard = (u: AdminUser): ReactElement => (
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="font-medium">{u.display_name}</div>
        <div className="mt-0.5 text-sm text-[var(--g-secondary)]">{u.email}</div>
        <div className="mt-1 text-xs text-[var(--g-secondary)]">
          {formatDate(u.created_at)}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        {statusPill(u.status)}
        {rolePills(u.roles)}
      </div>
    </div>
  );

  return (
    <div data-testid="admin-users-page" className="flex flex-col gap-4">
      <h1 className="text-xl font-bold">{t("users.title")}</h1>

      <input
        type="search"
        data-testid="admin-users-search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("users.searchPlaceholder")}
        aria-label={t("common.search")}
        className="min-h-[44px] w-full max-w-md rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
      />

      <AdminTable<AdminUser>
        columns={columns}
        rows={rows}
        rowKey={(u) => u.id}
        renderCell={renderCell}
        renderMobileCard={renderMobileCard}
        rowAriaLabel={(u) => u.display_name}
        onRowActivate={(u) => setSelected(u)}
        testid="admin-users"
        isLoading={isLoading}
        isError={isError}
        emptyLabel={t("users.empty")}
        errorLabel={t("error.loadFailed")}
      />

      {hasNextPage ? (
        <button
          type="button"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className="mx-auto min-h-[44px] rounded-full bg-[var(--g-elevated)] px-6 py-2 text-sm font-medium text-[var(--g-secondary)] hover:bg-[var(--g-hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:opacity-50"
        >
          {isFetchingNextPage ? t("common.refreshing") : t("common.loadMore")}
        </button>
      ) : null}

      <RoleEditorDrawer user={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export default UserListPage;
